import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelectedVideoStream, useSettings } from "../tools";
import * as tf from "@tensorflow/tfjs";
import "@tensorflow/tfjs-backend-webgl";
import { SupportedModels, drawBokehEffect, createSegmenter, BodySegmenter } from "@tensorflow-models/body-segmentation";
import { VideoBackgroundEffect } from "../types";
import { createContextProvider } from "conference/components/meeting-page/room/widgets";
import { useChangeTo } from "conference/tools";
import { Logger, useLogger } from "@dvrt/web-phone-client";
import AsyncEventHandler from "conference/asyncEventHandler";
import Timeout = NodeJS.Timeout;

tf.setBackend("webgl");

const createVideoElement = (track: MediaStreamTrack): Promise<HTMLVideoElement> => {
    const el = document.createElement("video");
    el.srcObject = new MediaStream([track]);
    el.play();
    return new Promise<HTMLVideoElement>((resolve) => {
        el.addEventListener("loadeddata", () => {
            el.width = el.videoWidth;
            el.height = el.videoHeight;
            resolve(el);
        });
    });
};

const average = (arr: number[]) => arr.reduce((acc, v) => acc + v) / arr.length;

class EffectManager {
    private model: BodySegmenter;
    private inputTrack: MediaStreamTrack;
    private effect: VideoBackgroundEffect;
    private intervalId: Timeout;
    private fps: number;
    private readonly logger: Logger;
    private outputTrack: MediaStreamTrack;
    private initializing: boolean = false;
    private initializingEvent: AsyncEventHandler;
    renderTime: number = 0;

    constructor(logger: Logger) {
        this.logger = logger;
        this.initializingEvent = new AsyncEventHandler("InitializeEffect");
    }

    async loadModel() {
        if (this.model) return;

        this.model = await createSegmenter(SupportedModels.MediaPipeSelfieSegmentation, {
            runtime: "tfjs", // or "mediapipe"
            modelType: "general",
        });
    }

    changeEffect(effect: VideoBackgroundEffect) {
        if (!this.isAvailable) return;

        this.effect = effect;
    }

    async apply(stream: MediaStream, effect: VideoBackgroundEffect) {
        if (!this.isAvailable) return;

        if (!stream?.getVideoTracks().length) return;

        this.effect = effect;

        if (this.initializing) await this.initializingEvent.waitFor();

        this.initializing = true;

        if (this.inputTrack) {
            stream.removeTrack(stream.getVideoTracks()[0]);
            stream.addTrack(this.outputTrack);
        } else await this.initializeRender(stream);

        this.initializing = false;
        this.initializingEvent.trigger();
    }

    private async initializeRender(stream: MediaStream) {
        this.inputTrack = stream.getVideoTracks()[0];
        this.fps = this.inputTrack.getSettings().frameRate;

        const videoElement = await createVideoElement(this.inputTrack);

        const canvas = document.createElement("canvas");
        const newStream = canvas.captureStream(this.fps);
        stream.removeTrack(this.inputTrack);

        if (this.outputTrack?.readyState === "live") this.outputTrack.stop();

        this.outputTrack = newStream.getVideoTracks()[0];
        stream.addTrack(this.outputTrack);
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        this.startLoop(canvas, videoElement);
    }

    private startLoop(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
        const interval = 1000 / this.fps;

        this.logger.debug("draw interval:", interval);

        const renderTimes: number[] = [];
        let isDrawing = false;

        this.intervalId = setInterval(async () => {
            if (isDrawing) return;

            isDrawing = true;
            const startTime = performance.now();

            if (this.effect.type === "none") this.drawPassThrough(canvas, video);
            else await this.drawBokehEffect(canvas, video);

            renderTimes.push(performance.now() - startTime);

            if (renderTimes.length >= this.fps) {
                this.renderTime = average(renderTimes);
                renderTimes.length = 0;
            }

            isDrawing = false;
        }, interval);
    }

    private drawPassThrough(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0);
    }

    get isAvailable() {
        return window.videoBackgroundEffectsEnabled && window.CanvasCaptureMediaStreamTrack;
    }

    async drawBokehEffect(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
        await this.loadModel();
        const params = this.effect.params;

        const backgroundBlurAmount = params.level === "light" ? 5 : 10;
        const edgeBlurAmount = 5;
        const flipHorizontal = false;

        const segmentation = await this.model.segmentPeople(video);
        drawBokehEffect(canvas, video, segmentation, 0.5, backgroundBlurAmount, edgeBlurAmount, flipHorizontal);
    }

    stop() {
        this.inputTrack?.stop();
        this.inputTrack = null;
        clearInterval(this.intervalId);
    }
}

export const useVideoEffectManagerFactory = (): {
    applyEffects: (stream: MediaStream) => Promise<void>;
    available: boolean;
    getRenderTime: () => number | null;
} => {
    const settings = useSettings();
    const logger = useLogger("VideoEffectManager");
    const effectManager = useMemo(() => new EffectManager(logger), []);

    const applyEffects = async (stream: MediaStream) => {
        await effectManager.apply(stream, settings.videoBackgroundEffect);
    };

    useEffect(() => {
        effectManager.changeEffect(settings.videoBackgroundEffect);
    }, [JSON.stringify(settings.videoBackgroundEffect)]);

    const stop = () => effectManager.stop();

    useChangeTo(settings.video, false, stop);

    useEffect(() => stop, []);

    return {
        applyEffects,
        available: effectManager.isAvailable,
        getRenderTime: () => effectManager.renderTime,
    };
};

export const useSelectedEffectedVideoTrack = () => {
    /* this is a Mount Everest sized hack: if the background effect enabled, there are 2 video tracks are used, so closing them
     automatically resulting a black screen. need to implement a media stream registry or sg like that... */
    const selectedVideoStream = useSelectedVideoStream(!window.videoBackgroundEffectsEnabled);
    const videoEffectManager = useVideoEffectManager();
    const [track, setTrack] = useState<MediaStreamTrack>();

    useEffect(() => {
        if (selectedVideoStream?.getVideoTracks().length)
            videoEffectManager.applyEffects(selectedVideoStream).then(() => {
                setTrack(selectedVideoStream.getVideoTracks()[0]);
            });
    }, [selectedVideoStream?.getVideoTracks()[0]?.id]);

    return track;
};

type Manager = ReturnType<typeof useVideoEffectManagerFactory>;

const VideoEffectContext = createContext(undefined);

export const VideoEffectContextProvider = createContextProvider(VideoEffectContext, useVideoEffectManagerFactory);

export const useVideoEffectManager = (): Manager => useContext(VideoEffectContext);
